/**
 * Quality Co-Pilot per-user UI preferences.
 *
 * A thin, door-specific binding of the SHARED per-user boolean store in
 * components/agentConsole/prefStore.ts — so the Co-Pilot page (and its tests)
 * import the qcp-prefixed (`cm_qcp_autoplay_{userId}`) helpers unchanged.
 *
 * Auto-play of the spoken rundown defaults to OFF: a fresh or unauthenticated
 * session stays silent unless the user opts in (per Jess Finlaw's request,
 * 2026-07-22). Turning the header "Auto-play" pill on persists per user, so a
 * user who WANTS spoken replies keeps them across sessions; a different user on
 * the same browser gets their own (default-off) setting.
 */
import { makeBoolPrefStore } from '../components/agentConsole/prefStore';

const store = makeBoolPrefStore('cm_qcp_autoplay_');

/** Fresh / unauthenticated sessions do not auto-speak until the user opts in. */
export const AUTOPLAY_DEFAULT = false;

/** Per-user localStorage key for the Co-Pilot auto-play preference. */
export const qcpAutoplayKey = store.key;

/**
 * The user's saved auto-play preference, or {@link AUTOPLAY_DEFAULT} (off) when
 * nothing has been saved (new user / unauthenticated / storage blocked).
 */
export const readAutoplayPref = (userId: string | null | undefined): boolean =>
  store.readOr(userId, AUTOPLAY_DEFAULT);

/** Persist the auto-play preference for this user. */
export const writeAutoplayPref = store.write;
